import ImageHeading from "../components/ui/ImageHeading";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Layout, Image as ImageIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import LabCard from "../components/ui/Card/LabCard";
import useAxiosPublic from "../hooks/useAxiosPublic";
import { useQuery } from "@tanstack/react-query";
import LoadingPage from "../components/Layout/LoadingPage";

const Lab2 = () => {
  const [active, setActive] = useState("");
  const axiosPublic = useAxiosPublic();

  const {
    data: labApiData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["labData"],
    queryFn: async () => {
      const res = await axiosPublic.get(
        "/academics/zums-technology-innovation-lab"
      );
      return res.data?.data;
    },
  });

  // Set the first category as active when data loads
  useEffect(() => {
    if (labApiData?.labs && !active) {
      const firstCategory = Object.keys(labApiData.labs)[0];
      setActive(firstCategory);
    }
  }, [labApiData, active]);

  if (isLoading) return <LoadingPage />;

  if (error) {
    return (
      <div className="text-center py-20 text-red-500">
        Error loading data: {error.message}
      </div>
    );
  }

  // Handle case where data might not be available
  if (!labApiData?.labs) {
    return (
      <div className="text-center py-20 text-gray-500">
        No lab data available
      </div>
    );
  }

  const tabs = Object.keys(labApiData.labs).map((key) => ({
    id: key,
    label: key,
    icon: key === "Engineering Applied Sciences" ? Layout : ImageIcon,
  }));

  const labCards = labApiData.labs;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200">
      <ImageHeading
        heading={labApiData?.hero?.title}
        imageUrl={labApiData?.hero?.media_url}
      />

      <div className="px-4 py-12 mx-auto max-w-7xl">
        <div
          className={cn(
            "mb-8 flex flex-row items-center justify-center overflow-scroll [perspective:1000px] relative sm:overflow-visible h-full no-visible-scrollbar max-w-full w-full"
          )}
        >
          <div className="px-8 py-4 bg-white rounded-md shadow-xl lg:rounded-full w-fit">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActive(tab.id)}
                className={cn("relative px-4 py-2 rounded-full")}
                style={{ transformStyle: "preserve-3d" }}
              >
                {active === tab.id && (
                  <motion.div
                    layoutId="clickedbutton"
                    transition={{ type: "spring", bounce: 0.3, duration: 0.6 }}
                    className={cn(
                      "absolute inset-0 bg-primary text-white rounded-full"
                    )}
                  />
                )}

                <span
                  className={`relative block ${
                    active === tab.id ? "text-white" : "text-black"
                  }`}
                >
                  {tab.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          <AnimatePresence mode="wait">
            {labCards[active]?.map((card) => (
              <LabCard key={card.id} {...card} />
            ))}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Lab2;
